/**
 * Pure footer-button logic for the Create/Edit Location wizard, extracted so the
 * role/status rules can be unit-tested in isolation from the React component.
 *
 * The secondary button is always "Save" (persist pending edits, stay on the
 * page). Keeping one meaning in that slot on every step is deliberate: the
 * previous last-step-only "Save as Draft" saved *and exited*, so the same
 * position would have taught two different behaviours.
 *
 * The primary button is driven by BOTH the user's role and the location's
 * persisted status (live vs draft):
 * - Intermediate steps show "Next" (advances + auto-saves as DRAFT).
 * - A supplier finishing a NEW / draft listing gets "Submit for Review".
 * - A supplier finishing an already-LIVE listing gets "Finish" (save preserves
 *   live = true — an intermediate save must never revert live -> false).
 * - An admin finishing the admin-only step gets "Finish"; live status there is
 *   governed exclusively by the Location Live toggle in that step.
 */

/** Discrete footer action the container maps to a wizard handler. */
export type WizardFooterAction = 'next' | 'finish' | 'save' | 'submitForReview';

export interface WizardFooterButton {
  label: string;
  action: WizardFooterAction;
}

export interface WizardFooterConfig {
  /** Primary (right-most) button. */
  primary: WizardFooterButton;
  /** Secondary button shown to the left of the primary button. Always "Save". */
  secondary: WizardFooterButton;
}

export interface WizardFooterParams {
  /** True when the current user is a SecurSpace admin. */
  isAdmin: boolean;
  /** Zero-based index of the active step. */
  currentStep: number;
  /** Zero-based index of the final step for this role (4 suppliers, 5 admins). */
  lastStepIndex: number;
  /** Persisted publish status of the location being edited (live === true). */
  isLive: boolean;
}

export const FOOTER_LABEL = {
  next: 'Next',
  finish: 'Finish',
  save: 'Save',
  submitForReview: 'Submit for Review',
} as const;

/**
 * Derives the wizard footer configuration from the user's role and the
 * location's persisted status.
 *
 * Rules:
 * - Every step -> "Save" (secondary); the container disables it until there
 *   are unsaved edits.
 * - Not on the final step -> "Next" (advances + auto-saves).
 * - Admin on the final (Admin Settings) step -> "Finish"; live status is governed
 *   exclusively by the Location Live toggle in that step.
 * - Supplier on the final (Pricing) step:
 *   - Editing a LIVE listing -> "Finish" (save preserves live = true).
 *   - New / draft listing -> "Submit for Review".
 */
export function getWizardFooterConfig(params: WizardFooterParams): WizardFooterConfig {
  const { isAdmin, currentStep, lastStepIndex, isLive } = params;
  const isLastStep = currentStep === lastStepIndex;
  const secondary: WizardFooterButton = { label: FOOTER_LABEL.save, action: 'save' };

  if (!isLastStep) {
    return { primary: { label: FOOTER_LABEL.next, action: 'next' }, secondary };
  }

  if (isAdmin) {
    return { primary: { label: FOOTER_LABEL.finish, action: 'finish' }, secondary };
  }

  if (isLive) {
    return { primary: { label: FOOTER_LABEL.finish, action: 'finish' }, secondary };
  }

  return {
    primary: { label: FOOTER_LABEL.submitForReview, action: 'submitForReview' },
    secondary,
  };
}
